<template>
  <div>
    <PageTitle :title="$t('global.upgrade-your-plan')" />
    <div class="row">
      <div class="col-md-9">
        <Card :title="$t('global.review-your-upgrade')">
          <div class="mb-4">
            <h4 class="text-primary mb-4">{{ $t("global.select-your-package") }}</h4>
            <LoadingState v-if="loadingPackages" />
            <FormPackageSelect
              v-else
              v-model="packageId"
              @input="onPackageSelected"
              :packages="packages"
              :plan-site-count="1"
              :plan-term="1"
            />
          </div>
          <div class="row mb-4">
            <div class="col-md-12">
              <h3 class="text-primary">{{ $t("global.billing-contact") }}</h3>
            </div>
            <div class="col-md-6">
              <FormInput
                name="first"
                v-model="addressFields.firstName"
                :label="`${$t('global.first-name')} *`"
                :errors="addressValidation.fields.firstName.errors"
                :dirty="addressValidation.fields.firstName.dirty"
                :disabled="submitting"
                class="pb-2"
              />
            </div>
            <div class="col-md-6">
              <FormInput
                name="last"
                v-model="addressFields.lastName"
                :label="`${$t('global.last-name')} *`"
                :errors="addressValidation.fields.lastName.errors"
                :dirty="addressValidation.fields.lastName.dirty"
                :disabled="submitting"
                class="pb-2"
              />
            </div>
            <div class="col-md-6">
              <FormInput
                name="phone"
                v-model="addressFields.phone"
                :label="`${$t('global.phone')} *`"
                :errors="addressValidation.fields.phone.errors"
                :dirty="addressValidation.fields.phone.dirty"
                :disabled="submitting"
                class="pb-2"
              />
            </div>
            <div class="col-md-6">
              <FormInput
                name="company"
                v-model="addressFields.company"
                :label="`${$t('global.company')}`"
                :disabled="submitting"
                class="pb-2"
              />
            </div>
            <div class="col-md-6">
              <FormInput
                name="address"
                v-model="addressFields.address1"
                :label="`${$t('global.address')} *`"
                :errors="addressValidation.fields.address1.errors"
                :dirty="addressValidation.fields.address1.dirty"
                :disabled="submitting"
                class="pb-2"
              />
            </div>
            <div class="col-md-6">
              <FormInput
                name="address2"
                v-model="addressFields.address2"
                :label="$t('global.address2')"
                :disabled="submitting"
                class="pb-2"
              />
            </div>
            <div class="col-md-6">
              <FormInput
                name="city"
                v-model="addressFields.city"
                :label="`${$t('global.city')} *`"
                :errors="addressValidation.fields.city.errors"
                :dirty="addressValidation.fields.city.dirty"
                :disabled="submitting"
                class="pb-2"
              />
            </div>
            <div class="col-md-6">
              <FormSelect
                name="state"
                v-model="addressFields.state"
                :label="`${$t('global.state')} *`"
                :errors="addressValidation.fields.state.errors"
                :dirty="addressValidation.fields.state.dirty"
                :disabled="submitting"
              >
                <option selected="true" value=""> - {{ $t("global.select-state") }} - </option>
                <option :value="state.code" v-for="state in states" :key="state.code">
                  {{ state.name }}
                </option>
              </FormSelect>
            </div>
            <div class="col-md-6">
              <FormInput
                name="zip"
                v-model="addressFields.zip"
                :label="`${$t('global.zip')} *`"
                :errors="addressValidation.fields.zip.errors"
                :dirty="addressValidation.fields.zip.dirty"
                :disabled="submitting"
                class="pb-2"
              />
            </div>
            <div class="col-md-6">
              <FormSelect
                name="country"
                v-model="addressFields.country"
                :label="`${$t('global.country')} *`"
                :errors="addressValidation.fields.country.errors"
                :dirty="addressValidation.fields.country.dirty"
                :disabled="submitting"
              >
                <option selected="true" value=""> - {{ $t("global.select-country") }} - </option>
                <option v-for="country in countries" :value="country.code" :key="country.code">
                  {{ country.name }}
                </option>
              </FormSelect>
            </div>
          </div>

          <!-- tax exmption -->
          <div v-if="showTaxExemptionForm" class="form-row mb-4 d-flex flex-column">
            <Tooltip :content="$t('global.tax-status-info')">
              <div class="d-flex">
                <h3 class="text-primary">{{ $t("global.tax-status") }}</h3>
                <Icon icon="info" color="gamma" class="ml-2" />
              </div>
            </Tooltip>
            <FormCheckbox
              v-model="addressFields.isTaxExempt"
              :label="$t('global.tax-exempt')"
              @input="resetTaxExemptForm"
              :disabled="submitting"
            />
            <template v-if="addressFields.isTaxExempt">
              <FormInput
                class="col-md-6"
                name="tax-exemption-id"
                v-model="addressFields.taxId"
                test-id="tax-exemption-id-input"
                :label="`${$t('global.tax-exemption-id')} *`"
                :errors="addressValidation.fields.taxId.errors"
                :dirty="addressValidation.fields.taxId.dirty"
                :disabled="submitting"
              />
            </template>
          </div>

          <!-- cc card -->
          <div class="mb-4">
            <h3 class="text-primary">{{ $t("global.payment-information") }}</h3>
            <div class="form-row">
              <FormInput
                class="col-md-12"
                name="number"
                v-model="paymentFields.number"
                test-id="cc-number"
                :label="`${$t('global.cc-number')} *`"
                :errors="primaryCard ? false : paymentValidation.fields.number.errors"
                :dirty="paymentValidation.fields.number.dirty"
                :disabled="!!primaryCard"
              />
            </div>
            <div class="form-row">
              <FormInput
                v-if="!!primaryCard"
                class="col-md-6"
                name="csv"
                v-model="paymentFields.expiration"
                test-id="csv"
                :label="`${$t('global.expiration')} *`"
                :disabled="true"
              />
              <FormExpirationMonth
                v-else
                class="col-md-6"
                name="expiration"
                v-model="paymentFields.expiration"
                test-id="expiration"
                :placeholder="'MM / YYYY'"
                :label="`${$t('global.expiration')} *`"
                :errors="primaryCard ? false : paymentValidation.fields.expiration.errors"
                :dirty="paymentValidation.fields.expiration.dirty"
                :disabled="submitting"
              />
              <FormInput
                class="col-md-6"
                name="csv"
                v-model="paymentFields.csv"
                test-id="csv"
                :label="`${$t('global.csv')} *`"
                :errors="primaryCard ? false : paymentValidation.fields.csv.errors"
                :dirty="paymentValidation.fields.csv.dirty"
                :disabled="!!primaryCard || submitting"
              />
            </div>
          </div>

          <CouponForm :amount="due" term="monthly" :package-id="packageId" @verify="applyCoupon" />
        </Card>
      </div>
      <div class="col-md-3" v-if="selectedPackage">
        <Card :disable-title="true" :height100="false">
          <LoadingState class="my-4" v-if="loading || loadingTax" />
          <template v-else>
            <CheckoutSummary
              :title="`${selectedApp.name} ${selectedPackage.name}`"
              :package="selectedPackage"
              :nickname="plan.nickname"
              :logo="selectedApp.type"
              :app-name="selectedApp.name"
              :location="plan.location"
              term="monthly"
              :show-term="false"
              :coupon="couponCode"
              :discount="discount"
              :tax="tax"
              :total="total"
            />
            <div class="text-right mt-4">
              <Btn variant="primary" :loading="submitting" :disabled="submitting" @click="submit">
                {{ $t("global.upgrade-plan") }}
              </Btn>
            </div>
          </template>
        </Card>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref } from "@vue/composition-api";
import Btn from "@/components/display/Btn";
import Card from "@/components/display/Card";
import CheckoutSummary from "@/components/display/checkout/CheckoutSummary";
import constants from "@/utils/constants";
import CouponForm from "@/components/display/CouponForm";
import { format } from "date-fns";
import FormCheckbox from "@/components/display/form/FormCheckbox";
import FormExpirationMonth from "@/components/display/form/FormExpirationMonth";
import FormInput from "@/components/display/form/FormInput";
import FormPackageSelect from "@/components/display/form/FormPackageSelect";
import FormSelect from "@/components/display/form/FormSelect";
import Icon from "@/components/display/icon/Icon";
import { isTaxExemptCountry } from "leatherman-js/utils/validations/rules/taxExempt";
import LoadingState from "@/components/display/LoadingState";
import PageTitle from "@/components/display/PageTitle";
import { addCreditCard as paymentValidationRules } from "leatherman-js/utils/validations/forms";
import store from "@/store";
import Tooltip from "@/components/display/Tooltip";
import useValidator from "@/utils/useValidator";
import { checkoutBilling as validationRules } from "leatherman-js/utils/validations/forms";

export default {
  name: "FreeTrialUpgrade",
  components: {
    PageTitle,
    LoadingState,
    Card,
    FormInput,
    FormSelect,
    FormCheckbox,
    Tooltip,
    Icon,
    FormExpirationMonth,
    CouponForm,
    CheckoutSummary,
    Btn,
    FormPackageSelect,
  },
  props: {
    planId: Number,
    appId: Number,
  },
  setup(props, { root }) {
    const loading = ref(true);
    const user = computed(() => store.getters["auth/getUser"]);
    const selectedApp = computed(() => store.getters["apps/getApp"]);
    const plan = computed(() => store.getters["plans/getItem"]);
    const couponCode = computed(() => store.getters["checkout/getCouponCode"]);
    const discount = computed(() => store.getters["checkout/getDiscount"]);
    const tax = computed(() => store.getters["checkout/getTax"]);
    const primaryCard = computed(() => store.getters["creditcards/primary"]);
    const isTaxExempt = computed(() => store.getters["tax/getIsTaxExempt"]);
    const billingAddress = computed(() => store.getters["addresses/getBillingAddress"]);
    const getUpgradePrice = (pkg) =>
      upgradePrices.value.find(({ packageId }) => packageId === pkg.id)?.amount || 0;
    const due = computed(() =>
      selectedPackage.value ? getUpgradePrice(selectedPackage.value) : 0
    );
    const total = computed(() => due.value + tax.value - discount.value);
    const submitting = ref(false);
    const countries = Object.keys(constants.COUNTRIES).map((code) => {
      return { code, name: constants.COUNTRIES[code] };
    });
    const states = Object.keys(constants.STATES).map((code) => {
      return { code, name: constants.STATES[code] };
    });

    const packageId = ref(null);
    const packagesStatus = computed(() => store.getters["appPackages/getListStatus"]);
    const upgradePrices = ref(null);
    const packages = computed(() => {
      if (!upgradePrices.value) {
        return [];
      }
      const pkgs = store.getters["appPackages/getPackages"];
      const upgradeableIds = upgradePrices.value.map(({ packageId }) => packageId);
      return pkgs.filter(({ id, app }) => {
        return upgradeableIds.includes(id) && selectedApp.value?.id === app.id;
      });
    });
    const selectedPackage = computed(() => {
      if (!packages.value) {
        return null;
      }
      store.dispatch("checkout/setDiscount", null);
      store.dispatch("checkout/setTax", null);
      return packages.value.find(
        ({ id, app }) => packageId.value === id && selectedApp.value?.id === app.id
      );
    });

    const { validator } = useValidator();
    const addressFields = reactive({
      firstName: null,
      lastName: null,
      email: user.value?.email,
      phone: null,
      address1: null,
      address2: null,
      city: null,
      state: null,
      zip: null,
      country: null,
      isTaxExempt: false,
      taxId: null,
    });
    const addressValidation = validator(
      addressFields,
      validationRules,
      {},
      {
        firstName: {
          required: root.$t("validation.first-name-required"),
        },
        lastName: {
          required: root.$t("validation.last-name-required"),
        },
        phone: {
          required: root.$t("validation.phone-required"),
          regex: root.$t("validation.invalid-phone"),
        },
        address1: {
          required: root.$t("validation.address-required"),
        },
        city: {
          required: root.$t("validation.city-required"),
        },
        state: {
          required: root.$t("validation.state-required"),
          regex: root.$t("validation.invalid-state"),
        },
        zip: {
          required: root.$t("validation.zip-required"),
          regex: root.$t("validation.invalid-zip"),
        },
        country: {
          required: root.$t("validation.country-required"),
          regex: root.$t("validation.invalid-country"),
        },
        taxId: {
          requiredIf: root.$t("validation.tax-id-required"),
          taxExempt: root.$t("validation.invalid-tax-id"),
        },
      }
    );
    const showTaxExemptionForm = computed(() => isTaxExemptCountry(addressFields.country));
    const resetTaxExemptForm = () => addressValidation.reset();

    // form fields
    const paymentFields = reactive({
      number: null,
      expiration: null,
      csv: null,
    });
    const paymentValidation = validator(
      paymentFields,
      paymentValidationRules,
      {},
      {
        number: {
          required: root.$t("validation.cc-number-required"),
          regex: root.$t("validation.invalid-cc-number"),
        },
        expiration: {
          required: root.$t("validation.cc-expiration-required"),
          regex: root.$t("validation.invalid-cc-expiration"),
        },
        csv: {
          required: root.$t("validation.csv-required"),
          regex: root.$t("validation.invalid-csv"),
        },
      }
    );

    const loadingTax = ref(false);
    const onPackageSelected = () => {
      store.dispatch("checkout/setApp", selectedApp.value.id);
      store.dispatch("checkout/setPackage", selectedPackage.value.id);
      store.dispatch("checkout/setTerm", "monthly");
    };

    // check if coupon is valid and add to store
    const applyCoupon = (couponCode, discountAmount) => {
      store.dispatch("checkout/setCouponCode", couponCode);
      store.dispatch("checkout/setDiscount", discountAmount);
      store.dispatch("checkout/calculateTax", { planId: plan.value.id });
    };

    const formatExpiration = (timestamp) => format(timestamp * 1000, "MM/yyyy");

    const submit = () => {
      submitting.value = true;
      addressValidation
        .validate()
        .then(() => {
          if (primaryCard.value) return Promise.resolve();
          return paymentValidation.validate();
        })
        .then(() => {
          // if billing address already exists, update it instead of creating a new one
          if (billingAddress.value) {
            // include the existing address' id in the request
            return store.dispatch("addresses/update", {
              id: billingAddress.value.id,
              data: addressFields,
            });
          } else {
            // hardcode the address type to "billing"
            return store.dispatch("addresses/create", {
              type: "billing",
              ...addressFields,
            });
          }
        })
        .then(() => {
          // check if the tax exemption checkbox is selected
          // if it is, validate the tax id provided
          if (!addressFields.isTaxExempt) {
            return Promise.resolve();
          }

          return store
            .dispatch("tax/validateTaxId", {
              taxId: addressFields.taxId,
              countryCode: addressFields.country,
            })
            .then((resp) => {
              // if the tax is valid for the country provided
              if (resp.verified) {
                return Promise.resolve();
              }
              addressValidation.fields.taxId.dirty = true;
              addressValidation.fields.taxId.errors = [root.$t("global.invalid-tax-id")];
              return Promise.reject();
            })
            .then(() =>
              store.dispatch("tax/setTaxId", {
                taxId: addressFields.taxId,
              })
            );
        })
        .then(() => {
          loadingTax.value = true;
          return store.dispatch("checkout/calculateTax", { planId: plan.value.id }).then(() => {
            loadingTax.value = false;
            return Promise.resolve();
          });
        })
        .then(() => {
          if (primaryCard.value) return Promise.resolve();
          // create credit card
          const name = `${addressFields.firstName} ${addressFields.lastName}`;
          return store.dispatch("creditcards/create", {
            name,
            ...paymentFields,
          });
        })
        .then(() => {
          // upgrade free trial
          return store.dispatch("plans/updatePackage", {
            id: props.planId,
            data: {
              packageId: packageId.value,
              couponCode: couponCode.value,
            },
          });
        })
        .then(() => {
          root.$router.push({
            name: "free-trial-upgrade-thank-you",
            query: { planId: plan.value.id },
          });
        })
        .catch(() => {
          store.dispatch("toasts/addToast", {
            variant: "error",
            message: root.$t("toasts.free-trial-creation-error-message"),
            timeout: true,
          });
        })
        .finally(() => {
          submitting.value = false;
        });
    };

    const loadingPackages = computed(() => packagesStatus.value.isLoading || loading.value);

    onMounted(() => {
      store.dispatch("addresses/getList").then(() => {
        // if a billing address already exists
        if (billingAddress.value) {
          // pre-populate the form with the existing billing address
          // computed values aren't allowed as defaults, which is why these are being set down here after the fetch has completed
          Object.keys(addressFields).forEach((addressKey) => {
            addressFields[addressKey] = billingAddress.value[addressKey];
          });

          if (isTaxExempt.value) {
            addressFields.isTaxExempt = true;
            addressFields.taxId = taxId.value;
          }
        }
      });

      store.dispatch("creditcards/getList").then(() => {
        if (primaryCard.value) {
          paymentFields.number = primaryCard.value.number;
          paymentFields.expiration = formatExpiration(primaryCard.value.expiration);
          paymentFields.csv = "***";
        }
      });
      store.dispatch("checkout/cartstackActive", false);
      store.dispatch("checkout/setCouponCode", null);
      store.dispatch("checkout/setDiscount", 0);
      store.dispatch("checkout/setTerm", "monthly");
      store
        .dispatch("plans/getItem", props.planId)
        .then(() => store.dispatch("appPackages/getList"))
        .then(() => store.dispatch("locations/getList"))
        .then(() => store.dispatch("apps/get", props.appId))
        .then(() => {
          store
            .dispatch("plans/getUpgradePrices", {
              planId: props.planId,
              appId: plan.value.isTShirt ? undefined : selectedApp.value.id,
              isTShirt: plan.value.isTShirt,
              isFreeTrial: true,
            })
            .then((list) => {
              upgradePrices.value = list;
            })
            .finally(() => {
              loading.value = false;
            });
        });
    });

    return {
      selectedPackage,
      plan,
      selectedApp,
      loading,
      paymentFields,
      addressFields,
      showTaxExemptionForm,
      resetTaxExemptForm,
      addressValidation,
      paymentValidation,
      applyCoupon,
      submit,
      submitting,
      countries,
      states,
      couponCode,
      discount,
      tax,
      total,
      packagesStatus,
      packages,
      packageId,
      due,
      loadingPackages,
      loadingTax,
      onPackageSelected,
      primaryCard,
    };
  },
};
</script>
